import { useStrings } from "@1js/localization-components";
import {
  Accordion,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  makeStyles,
  type AccordionToggleEventHandler,
} from "@fluentui/react-components";
import { Dismiss24Regular, Filter20Regular } from "@fluentui/react-icons";
import * as React from "react";
import {
  MobileApplyFilterLabel,
  MobileClearFilterLabel,
  MobileFilterButtonLabel,
  MobileFilterLabel,
} from "./PromptFilter.strings";
import { PromptFilterAccordionItem } from "./PromptFilterAccordionItem";
import { PromptFiltersSkeleton } from "./PromptFilterSkeleton";
import type { Filter } from "./types";

const useStyles = makeStyles({
  container: {
    display: "flex",
    alignItems: "flex-start",
    alignSelf: "stretch",
    justifyContent: "center",
    gap: "0.5rem",

    "@media (max-width: 540px)": {
      alignItems: "center",
    },
  },

  dialogBody: {
    maxHeight: "calc(100vh - 104px)",
  },

  actions: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    paddingTop: "16px",
    paddingBottom: "16px",
  },
});

export type PromptFiltersMobileProps = {
  /**
   * List of filters.
   */
  filters: Filter[];

  /**
   * Selected values for each filter.
   */
  selectedFilters?: Record<string, string[]>;

  /**
   * Default selected values for each filter.
   */
  defaultSelectedFilters?: Record<string, string[]>;

  /**
   * Optional loading state to be applied to the root element. If true, a skeleton view will be rendered.
   */
  isLoading?: boolean;

  /**
   * Callback to be called when the filter changes. Make sure no customer data is being passed back. The host usually logs the filters, so make sure no sensitive data is being passed back.
   * @param selectedValues The new filters
   * @filters The changed filters
   * @returns void
   */
  onApplyFiltered: (
    selectedValues: Record<string, string[]>,
    filters: Filter[]
  ) => void;

  /**
   * Optional callback to be called when the filter is opened or closed.
   * @param isOpen Whether filter is open or closed
   * @param filters The filters that changed
   * @returns void
   */
  onOpenChanged?: (isOpen: boolean, filters: Filter[]) => void;

  /**
   * Filters have been cleared.
   * @returns void
   */
  onCleared?: () => void;
};

export const PromptFiltersMobile: React.FC<PromptFiltersMobileProps> = ({
  filters,
  selectedFilters,
  isLoading,
  onApplyFiltered,
  onOpenChanged,
  onCleared,
}) => {
  const styles = useStyles();
  const [openItems, setOpenItems] = React.useState<string[]>([]);
  const [values, setValues] = React.useState(selectedFilters ?? {});

  React.useEffect(() => {
    setValues(selectedFilters ?? {});
  }, [selectedFilters]);

  const localizations = useStrings({
    MobileFilterButtonLabel,
    MobileFilterLabel,
    MobileClearFilterLabel,
    MobileApplyFilterLabel,
  });

  React.useEffect(() => {
    setOpenItems(filters.map((filter) => filter.key));
  }, [filters]);

  const handleClearAllClicked = React.useCallback(() => {
    setValues({});
    onCleared?.();
  }, [onCleared]);

  const handleApplyClicked = React.useCallback(() => {
    const changedFilters: Filter[] = [];
    for (const filter of filters) {
      if (values[filter.key] !== selectedFilters?.[filter.key]) {
        changedFilters.push(filter);
      }
    }
    onApplyFiltered(values, changedFilters);
  }, [filters, onApplyFiltered, selectedFilters, values]);

  const handleToggle: AccordionToggleEventHandler = React.useCallback(
    (_event, data) => {
      setOpenItems((prevOpenItems) => {
        const itemIndex = prevOpenItems.indexOf(data.value as string);
        if (itemIndex === -1) {
          return [...prevOpenItems, data.value as string];
        } else {
          return prevOpenItems.filter((_, index) => index !== itemIndex);
        }
      });
    },
    []
  );

  const handleOnOpenChange = React.useCallback(() => {
    onOpenChanged?.(true, filters);
  }, [filters, onOpenChanged]);

  if (isLoading) {
    return <PromptFiltersSkeleton />;
  }

  return (
    <Dialog onOpenChange={handleOnOpenChange}>
      <DialogTrigger disableButtonEnhancement>
        <div className={styles.container}>
          <Button
            data-testid="mobile-filter-button"
            icon={<Filter20Regular />}
            size="medium"
            aria-label={localizations.MobileFilterButtonLabel()}
          />
        </div>
      </DialogTrigger>
      <DialogSurface>
        <DialogBody className={styles.dialogBody}>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button
                  appearance="subtle"
                  aria-label="close"
                  icon={<Dismiss24Regular />}
                />
              </DialogTrigger>
            }
          >
            {localizations.MobileFilterLabel()}
          </DialogTitle>
          <DialogContent>
            <Accordion
              multiple
              collapsible
              openItems={openItems}
              onToggle={handleToggle}
            >
              {filters.map((filter: Filter) => (
                <PromptFilterAccordionItem
                  key={filter.key}
                  filter={filter}
                  selectedValues={values?.[filter.key]}
                  onChange={(_, newValue) => {
                    setValues((prevValues) => ({
                      ...prevValues,
                      [filter.key]: newValue,
                    }));
                  }}
                />
              ))}
            </Accordion>
          </DialogContent>
        </DialogBody>
        <DialogActions className={styles.actions}>
          <Button
            appearance="subtle"
            size="large"
            onClick={handleClearAllClicked}
          >
            {localizations.MobileClearFilterLabel()}
          </Button>
          <DialogTrigger action="close">
            <Button
              appearance="outline"
              size="large"
              onClick={handleApplyClicked}
            >
              {localizations.MobileApplyFilterLabel()}
            </Button>
          </DialogTrigger>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};
